/**
 * Gallery templates — shared types.
 *
 * GAL-10. A gallery renders through ONE of 23 designs (the studio's
 * choice, stored on `galleries.template`) plus a small set of curated
 * visual preferences (`galleries.customization`, jsonb). Both ride
 * the public payload, so nothing in here may ever be a secret — the
 * PIN, the download toggle and the watermark toggle are real columns
 * and are gated server-side.
 *
 * The 23 ids match the handoff numbering in
 * design_handoff_client_galleries/README.md (01 Editorial … 23 Story
 * Reel) and the CHECK constraint in migration 20260904120000.
 * GAL-13 retired 'classic' (the pre-design PublicGallery look):
 * every gallery now renders a design, and Editorial (01) is the
 * default for rows created without one.
 *
 * GALLERY-2 (Oct 2026) — the handoff's 00 Basic joins as the 24th
 * design and the default for new galleries (migration
 * 20261007150000_gallery2_basic.sql adds it to the CHECK and makes it
 * the column default); every design gains a background and a font
 * pairing; Basic adds its cover's height and dim.
 */

// GALLERY-ONE — the 23 designs are gone. One gallery, customized by the
// studio, is the whole product now: background, fonts, cover and
// stickers instead of a catalogue of fixed looks. Rennan, 28 Oct:
// "id rather focus on a much more simple gallery than these random ones
// that are not being chosen."
//
// The id stays a union rather than a bare string so the column, the
// renderer and the iOS catalog keep agreeing, and so adding a second
// one later is a one-line change rather than an archaeology project.
export const PHOTO_TEMPLATE_IDS = [
  'basic',
] as const;

/** Film galleries render through Basic too; the four video designs are gone. */
export const VIDEO_TEMPLATE_IDS = [] as const;

export const GALLERY_TEMPLATE_IDS = [
  ...PHOTO_TEMPLATE_IDS,
  ...VIDEO_TEMPLATE_IDS,
] as const;

export type PhotoTemplateId = (typeof PHOTO_TEMPLATE_IDS)[number];
export type VideoTemplateId = (typeof VIDEO_TEMPLATE_IDS)[number];
export type GalleryTemplateId = PhotoTemplateId | VideoTemplateId;

export const isGalleryTemplateId = (v: unknown): v is GalleryTemplateId =>
  typeof v === 'string' && (GALLERY_TEMPLATE_IDS as readonly string[]).includes(v);

/**
 * Curated customization — the studio's knobs, deliberately few:
 *
 *   accent          hex override for the design's signature accent.
 *                   null/undefined = the design's own accent.
 *   useBrandColor   when true the studio's org brand colour becomes
 *                   the accent (wins over `accent`).
 *   fonts.display   Google Font family override for headlines.
 *   fonts.body      Google Font family override for text.
 *                   null/undefined = the design's own fonts.
 *   slideshow       on/off + seconds per frame (2–10). Photo designs.
 *
 * Everything is optional; a `{}` customization renders the design
 * exactly as the handoff prototype does.
 *
 * A `type` alias rather than an `interface` on purpose: the generated
 * Supabase `Json` type needs an index signature, and TypeScript gives
 * object-literal types one implicitly (interfaces get none), so this
 * stays assignable to the `customization` column without loosening
 * it to `[key: string]: unknown`.
 */
export type GalleryCustomization = {
  accent?: string | null;
  /**
   * GALLERY-ONE — any colour, not just one of the sixteen grounds.
   * Wins over `background` when set. Hex only, checked with the same
   * HEX_COLOR the accent has always used: the grounds table exists so
   * the stored VALUE is never interpolated, and a `#rrggbb` regex is
   * what makes an exception to that safe. The ink, the secondary text
   * and the hairline are derived from it rather than stored, so a
   * studio can't pick an unreadable pair.
   */
  backgroundHex?: string | null;
  /**
   * GALLERY-ONE — film grain over the page. Three named steps rather
   * than a slider: a studio wants "a bit" or "a lot", and a percentage
   * invites fiddling with a thing that reads the same across ten
   * points of it.
   */
  grain?: GalleryGrain | null;
  useBrandColor?: boolean;
  fonts?: {
    display?: string | null;
    body?: string | null;
  };
  slideshow?: {
    enabled?: boolean;
    seconds?: number;
  };
  /**
   * GALLERY-2 — the page's ground and its ink, from a short list (the
   * handoff's `background` tweak). Absent = the design's own palette.
   * Accents and overlays stay the design's.
   */
  background?: GalleryBackgroundId | null;
  /**
   * GALLERY-2 — 00 Basic's cover: its height (vh, 40–100) and how far
   * it's dimmed under the title (%, 0–70). The photo is the gallery's
   * own cover (`cover_photo_id`), else the first photo.
   */
  // GALLERY-ONE — where the cover is anchored. Rennan chose full-bleed
  // over a band that follows the photo, so the cover IS a crop — it just
  // stops being a silent one: the studio drags to say what stays in
  // frame instead of inheriting a hardcoded 50% / 35%.
  cover?: {
    height?: number;
    dim?: number;
    /** Percent across and down the frame; 50 / 35 is the old constant. */
    focusX?: number;
    focusY?: number;
    /**
     * GALLERY-ONE — which of the four arrangements. Absent reads as
     * 'full', so every gallery saved before this existed keeps the cover
     * it already had.
     */
    layout?: GalleryCoverLayout | null;
  };
  /**
   * GALLERY-BAR — the strip across the top of the gallery.
   *
   * Rennan: *"this top bar on the galleries should be customizable
   * too. adding in your logo from your brand, have the bar or not, or
   * just your logo and the options, the download and all the options
   * should continue to be on there."*
   *
   * That last clause is the constraint the whole thing is built
   * around: whatever the studio turns off, the client can still
   * favourite, share and download. Turning the bar off moves the
   * actions; it never removes them.
   */
  bar?: {
    mode?: GalleryBarMode | null;
    /** The studio's brand logo. Absent reads as true. */
    logo?: boolean;
  };
};

/** GALLERY-2 — the grounds a studio can put a design on. */
// GALLERY-ONE — one gallery means the background does the work five
// designs used to. Ordered light → dark, warm before cool at each step,
// so the swatch row reads as a gradient rather than a crayon box.
// An id this list doesn't know is dropped by `isGalleryBackgroundId`
// below, so a gallery saved on a retired ground falls back to the
// design's own rather than breaking.
export const GALLERY_GRAINS = ['none', 'fine', 'heavy'] as const;
export type GalleryGrain = (typeof GALLERY_GRAINS)[number];
export const isGalleryGrain = (v: unknown): v is GalleryGrain =>
  typeof v === 'string' && (GALLERY_GRAINS as readonly string[]).includes(v);
/** How much of the noise shows, per step. */
export const GRAIN_OPACITY: Record<GalleryGrain, number> = { none: 0, fine: 0.055, heavy: 0.15 };

export const GALLERY_BACKGROUND_IDS = [
  'light', 'ivory', 'cream', 'sand', 'blush', 'sage', 'mist', 'grey', 'stone',
  'slate', 'espresso', 'forest', 'plum', 'ink', 'charcoal', 'black',
] as const;
export type GalleryBackgroundId = (typeof GALLERY_BACKGROUND_IDS)[number];
export const isGalleryBackgroundId = (v: unknown): v is GalleryBackgroundId =>
  typeof v === 'string' && (GALLERY_BACKGROUND_IDS as readonly string[]).includes(v);

/** GALLERY-2 — Basic's cover, the handoff's ranges and defaults. */
/**
 * GALLERY-ONE — the four cover arrangements.
 *
 * 'full' is what the cover has always been and stays the default: the
 * photograph edge to edge with the title over it. It is also the only
 * one of the four that crops, which is why the focal point exists.
 *
 * The other three came out of Rennan's own rule — a gallery never crops,
 * retouches or zooms — applied to the one surface that still broke it.
 * 'frame' and 'masthead' show the whole frame as shot; 'split' crops only
 * its own half.
 *
 *   full      photo edge to edge, title over it, dim behind the type
 *   frame     the whole photograph on the ground, title beneath it
 *   split     photograph one side, title on the ground the other
 *   masthead  title on the bare ground, photograph whole beneath it
 *
 * An id this list doesn't know is dropped on parse, so a gallery saved on
 * a retired arrangement falls back to 'full' rather than breaking.
 */
export const GALLERY_COVER_LAYOUTS = ['full', 'frame', 'split', 'masthead'] as const;
export type GalleryCoverLayout = (typeof GALLERY_COVER_LAYOUTS)[number];
export const isGalleryCoverLayout = (v: unknown): v is GalleryCoverLayout =>
  typeof v === 'string' && (GALLERY_COVER_LAYOUTS as readonly string[]).includes(v);
export const COVER_LAYOUT_DEFAULT: GalleryCoverLayout = 'full';
/**
 * Which of the cover's other controls each arrangement actually uses.
 * A slider that does nothing is worse than a missing one, so the panel
 * reads this rather than showing all three on every layout: the focal
 * point only means something where the photograph is cropped to a box,
 * and the dim only where the type sits on top of it.
 */
export const COVER_LAYOUT_USES: Record<GalleryCoverLayout, { focus: boolean; dim: boolean }> = {
  full: { focus: true, dim: true },
  frame: { focus: false, dim: false },
  split: { focus: true, dim: false },
  masthead: { focus: false, dim: false },
};
export const clampCoverLayout = (v: unknown): GalleryCoverLayout =>
  isGalleryCoverLayout(v) ? v : COVER_LAYOUT_DEFAULT;

/**
 * GALLERY-BAR — what the top of a gallery looks like.
 *
 *   full   the logo (or the studio's initial), the studio's name, and
 *          the actions. What every gallery has had until now.
 *   mark   the logo and the actions. No name — for a studio whose
 *          logo already says the name, where setting it twice is just
 *          saying it twice.
 *   float  no bar at all. The actions float over the photographs in a
 *          pill instead, so the photographs start at the top of the
 *          window and nothing is lost.
 *
 * An id this list doesn't know is dropped on parse, so a gallery saved
 * on a retired mode falls back to the bar it already had.
 */
export const GALLERY_BAR_MODES = ['full', 'mark', 'float'] as const;
export type GalleryBarMode = (typeof GALLERY_BAR_MODES)[number];
export const isGalleryBarMode = (v: unknown): v is GalleryBarMode =>
  typeof v === 'string' && (GALLERY_BAR_MODES as readonly string[]).includes(v);
export const BAR_MODE_DEFAULT: GalleryBarMode = 'full';
export const clampBarMode = (v: unknown): GalleryBarMode =>
  isGalleryBarMode(v) ? v : BAR_MODE_DEFAULT;
/**
 * Which modes have anywhere to put a logo. `float` has no bar to put
 * one in, so the panel hides the switch rather than offering one that
 * does nothing — the same reason COVER_LAYOUT_USES exists.
 */
export const BAR_MODE_USES_LOGO: Record<GalleryBarMode, boolean> = {
  full: true,
  mark: true,
  float: false,
};

export const COVER_HEIGHT_MIN = 40;
export const COVER_HEIGHT_MAX = 100;
export const COVER_HEIGHT_DEFAULT = 70;
export const COVER_DIM_MIN = 0;
export const COVER_DIM_MAX = 70;
export const COVER_DIM_DEFAULT = 30;
/** Focal point, in percent of the frame. 35% down is where a face
 *  usually sits in a portrait, which is why it was the old constant. */
export const COVER_FOCUS_X_DEFAULT = 50;
export const COVER_FOCUS_Y_DEFAULT = 35;

const clampStep = (n: unknown, min: number, max: number, fallback: number, step = 5): number => {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.round(n / step) * step : fallback;
  return Math.min(max, Math.max(min, v));
};
export const clampCoverHeight = (n: unknown): number =>
  clampStep(n, COVER_HEIGHT_MIN, COVER_HEIGHT_MAX, COVER_HEIGHT_DEFAULT);
export const clampCoverDim = (n: unknown): number =>
  clampStep(n, COVER_DIM_MIN, COVER_DIM_MAX, COVER_DIM_DEFAULT);
const clampPercent = (n: unknown, fallback: number): number => {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.min(100, Math.max(0, Math.round(v)));
};
export const clampFocusX = (n: unknown): number => clampPercent(n, COVER_FOCUS_X_DEFAULT);
export const clampFocusY = (n: unknown): number => clampPercent(n, COVER_FOCUS_Y_DEFAULT);

export const SLIDESHOW_MIN_SECONDS = 2;
export const SLIDESHOW_MAX_SECONDS = 10;
export const SLIDESHOW_DEFAULT_SECONDS = 4;

export const clampSlideshowSeconds = (n: unknown): number => {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : SLIDESHOW_DEFAULT_SECONDS;
  return Math.min(SLIDESHOW_MAX_SECONDS, Math.max(SLIDESHOW_MIN_SECONDS, v));
};

/** Defensive parse of the jsonb column — never trust its shape. */
export const parseCustomization = (raw: unknown): GalleryCustomization => {
  if (!raw || typeof raw !== 'object') return {};
  const c = raw as Record<string, unknown>;
  const out: GalleryCustomization = {};
  if (typeof c.accent === 'string' && HEX_COLOR.test(c.accent)) out.accent = c.accent;
  if (typeof c.backgroundHex === 'string' && HEX_COLOR.test(c.backgroundHex)) out.backgroundHex = c.backgroundHex;
  if (isGalleryGrain(c.grain)) out.grain = c.grain;
  if (typeof c.useBrandColor === 'boolean') out.useBrandColor = c.useBrandColor;
  if (c.fonts && typeof c.fonts === 'object') {
    const f = c.fonts as Record<string, unknown>;
    out.fonts = {
      display: safeFontFamily(f.display),
      body: safeFontFamily(f.body),
    };
  }
  if (c.slideshow && typeof c.slideshow === 'object') {
    const s = c.slideshow as Record<string, unknown>;
    out.slideshow = {
      enabled: typeof s.enabled === 'boolean' ? s.enabled : true,
      seconds: clampSlideshowSeconds(s.seconds),
    };
  }
  if (isGalleryBackgroundId(c.background)) out.background = c.background;
  if (c.cover && typeof c.cover === 'object') {
    const v = c.cover as Record<string, unknown>;
    out.cover = {
      height: clampCoverHeight(v.height),
      dim: clampCoverDim(v.dim),
      focusX: clampFocusX(v.focusX),
      focusY: clampFocusY(v.focusY),
      layout: clampCoverLayout(v.layout),
    };
  }
  if (c.bar && typeof c.bar === 'object') {
    const v = c.bar as Record<string, unknown>;
    out.bar = {
      mode: clampBarMode(v.mode),
      // Absent reads as true: every gallery saved before this existed
      // showed the logo when the studio had one, and a studio who has
      // never seen this control should not have their logo vanish.
      logo: typeof v.logo === 'boolean' ? v.logo : true,
    };
  }
  return out;
};

/**
 * SEC-XSS — the allowed faces, and why this list is the security boundary.
 *
 * A theme's font family is not decoration in the DOM sense: it is
 * interpolated into a CSS text block that ~20 designs build as template
 * literals (`.gal-zn .key{font-family:${display};…}`) and hand to
 * `TemplateStyle`, which renders it through `dangerouslySetInnerHTML`.
 *
 * So a family string is executable text. A literal `</style>` inside it
 * closes the element and everything after it parses as HTML; short of
 * that, plain CSS injection buys an attacker `background:url(https://…)`
 * exfiltration and full-page overlays. The victim is whoever opens the
 * shared gallery link, on our domain or on the studio's.
 *
 * `accent` was already regex-checked here. Fonts were not — they took any
 * string at all. They are now checked against the same list the picker
 * offers, which is the strongest available check: the UI only ever emits
 * one of these, so a whitelist costs legitimate studios nothing and
 * leaves no escaping question open.
 *
 * Adding a face means adding it here. The picker imports these; it does
 * not keep its own copy.
 */
// GALLERY-ONE — this list must be the INTERSECTION of what the web can
// fetch from Google Fonts and what iOS bundles in GalleryDesigns/Fonts.
// A face on one side only looks like a working choice and then renders
// as something else on the other platform, silently. 'Playfair Display'
// was here and is not bundled on iOS; it is removed rather than
// half-supported. Adding a face means adding the .ttf too.
export const DISPLAY_FONTS = [
  'Instrument Serif', 'Fraunces', 'Cormorant Garamond', 'DM Serif Display',
  'Bodoni Moda', 'Newsreader', 'EB Garamond', 'Syne', 'Archivo', 'Manrope', 'Bebas Neue',
  // GALLERY-2 — the faces the font pairings use (Basic's Sans, Mono).
  'Figtree', 'JetBrains Mono',
] as const;

export const BODY_FONTS = [
  'Inter', 'Familjen Grotesk', 'Karla', 'Work Sans', 'Figtree', 'Nunito Sans',
  // 'IBM Plex Sans' removed with Playfair, for the same reason.
  'Public Sans', 'Hanken Grotesk', 'Plus Jakarta Sans', 'Manrope', 'Outfit',
  // GALLERY-2 — the pairings' text faces (Editorial, Condensed, Mono).
  'Space Grotesk', 'JetBrains Mono',
] as const;

/**
 * GALLERY-2 — what each pickable face actually has on Google Fonts. An
 * override asks for these and nothing else: the css2 API answers a
 * request for a weight or an italic a family doesn't have with an error
 * for the WHOLE stylesheet, so "Bebas Neue, 400–700, italic" lost every
 * face the gallery asked for at once, the design's own included.
 */
export const FONT_STYLES: Readonly<Record<string, { weights: number[]; italic: boolean }>> = {
  'Instrument Serif': { weights: [400], italic: true },
  'Playfair Display': { weights: [400, 500, 600, 700], italic: true },
  Fraunces: { weights: [400, 500, 600, 700], italic: true },
  'Cormorant Garamond': { weights: [400, 500, 600, 700], italic: true },
  'DM Serif Display': { weights: [400], italic: true },
  'Bodoni Moda': { weights: [400, 500, 600, 700], italic: true },
  Newsreader: { weights: [400, 500, 600, 700], italic: true },
  'EB Garamond': { weights: [400, 500, 600, 700], italic: true },
  Syne: { weights: [400, 500, 600, 700], italic: false },
  Archivo: { weights: [400, 500, 600, 700], italic: true },
  Manrope: { weights: [400, 500, 600, 700], italic: false },
  'Bebas Neue': { weights: [400], italic: false },
  Figtree: { weights: [400, 500, 600, 700], italic: true },
  'JetBrains Mono': { weights: [400, 500, 600, 700], italic: true },
  Inter: { weights: [400, 500, 600, 700], italic: true },
  'Familjen Grotesk': { weights: [400, 500, 600, 700], italic: true },
  Karla: { weights: [400, 500, 600, 700], italic: true },
  'Work Sans': { weights: [400, 500, 600, 700], italic: true },
  'Nunito Sans': { weights: [400, 500, 600, 700], italic: true },
  'Public Sans': { weights: [400, 500, 600, 700], italic: true },
  'Hanken Grotesk': { weights: [400, 500, 600, 700], italic: true },
  'IBM Plex Sans': { weights: [400, 500, 600, 700], italic: true },
  'Plus Jakarta Sans': { weights: [400, 500, 600, 700], italic: true },
  Outfit: { weights: [400, 500, 600, 700], italic: false },
  'Space Grotesk': { weights: [400, 500, 600, 700], italic: false },
};

const ALLOWED_FONTS: ReadonlySet<string> = new Set<string>([...DISPLAY_FONTS, ...BODY_FONTS]);

/** A six-digit hex colour. The only colour shape that may reach CSS text. */
export const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * The family if we ship it, else null — null means "use the design's own
 * face", which is always what an unrecognised value should fall back to.
 * Exported because `resolveTheme` re-checks at the point of use: not
 * every customization object in the app came through `parseCustomization`
 * (demo payloads, direct construction), and the sink is too far from here
 * to rely on one caller having done the right thing.
 */
export const safeFontFamily = (value: unknown): string | null =>
  typeof value === 'string' && ALLOWED_FONTS.has(value) ? value : null;
